import { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as ActionEditIcon } from '../../assets/icons/action-edit.svg';
import { ReactComponent as DeleteIcon } from '../../assets/icons/delete-icon.svg';
import { ReactComponent as ActionDeleteIcon } from '../../assets/icons/delete-table.svg';
import { ReactComponent as ActionDeleteIconDark } from '../../assets/icons/iconsDark/action-delete-icon-dark.svg';
import { ReactComponent as LocationIcon } from '../../assets/icons/location-icon.svg';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import ThemeContext from '../../contexts/theme-context/ThemeContext';
import {
  DeleteFacilityTag,
  FacilityTags,
  RequestFacilityTagsInterFace,
  SelectedFacilityTag,
} from '../../interface/facility-tag.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  fetchFacilityTags,
  fetchListFilters,
  setFacilityTagsFilterData,
  setSortData,
  updateFacilityFilters,
} from '../../redux/slice/FacilityTagSlice';
import { setNotificationDetails } from '../../redux/slice/NotificationSlice';
import { RootState } from '../../redux/store';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeleteWarning from '../../shared-components/delete-warning/DeleteWarning';
import ModalBox from '../../shared-components/modal-box/ModalBox';
import PageTitle from '../../shared-components/page-title/PageTitle';
import SuccessNotification from '../../shared-components/success-notification/SuccessNotificaion';
import StationSidePanel from '../../shared-components/table-side-panel/StationSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import facilityTagApi from '../../utils/api/facility-tag';

import AddFacilityTag from './components/AddFacilityTag';
import EditFacilityTagComponent from './components/EditFacilityTag';

interface ModalBoxDetail {
  title: string | null;
  header: boolean;
  content: JSX.Element | null;
  notificationModal: boolean;
}
/**
 * Facility Tag management
 *
 */
function FacilityTagComponent() {
  const dispatch = useAppDispatch();
  const { theme } = useContext(ThemeContext);

  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    header: false,
    content: null,
    notificationModal: false,
  });
  const {
    facilityTags,
    page,
    limit,
    totalPages,
    totalResults,
    loading,
    filters,
    filterListLoading,
  } = useAppSelector((states) => states.facilityTags);
  const { currentFacilityTagsList } = useAppSelector((state) => state.facilityTags);
  const { sortBy, sortField } = useAppSelector((state: RootState) => state.facilityTags);
  const navigate = useNavigate();

  // /**
  //  * Handle the fetch data for Api call function
  //  * @param data
  //  */
  // const fetchData = (data: RequestFacilityTagsInterFace) => {
  //   if (!loading) {
  //     dispatch(fetchFacilityTags(data));
  //   }
  // };

  /**
   * Handle the fetch data for Api call function
   * @param data
   */
  const fetchData = (data: RequestFacilityTagsInterFace) => {
    const selectedFacility = currentFacilityTagsList.find((item) => item.isSelected);

    if (selectedFacility?._id && !loading) {
      dispatch(
        fetchFacilityTags({
          ...data,
        }),
      );
    }
  };

  /**
   * Function to handle data limit change
   */
  const handleLimitChange = (changeLimit: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page: DefaultApiLimit.page,
      limit: changeLimit,
    });
  };

  /**
   * Handle the modal close function
   */
  const handleCloseModal = () => {
    fetchData({
      page,
      limit,
    });
    setModalBoxStatus('close');
  };

  /**
   * Handles the deletion of a facility tag through the API
   *
   *
   */
  const handleDeleteFacilityTag = (id: string) => {
    facilityTagApi
      .deleteFacilityTag(id)
      .then(() => {
        setModalBoxStatus('open');
        setModalBoxDetail({
          title: null,
          header: false,
          content: (
            <SuccessNotification
              title="Facility deleted Successfully "
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={handleCloseModal}
            />
          ),
          notificationModal: true,
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationDetails({
            type: 'error',
            notificationDetails: {
              statusCode: error?.response?.data?.code || 200,
              message: error?.response?.data?.message || 'Something went wrong!',
            },
          }),
        );
      });
  };

  /**
   * Handles the pagination
   *
   */
  const handlePageChange = (changePage: number) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      sortField,
      sortBy,
      page: changePage,
      limit,
    });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(
      setFacilityTagsFilterData({
        ...sortData,
      }),
    );
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Displays a warning modal before deleting a facility tag
   * @param {DeleteFacilityTag} facilityTag - The facilityTag to be deleted
   */
  const handleDeleteFacilityTagWarning = (facilityTag: DeleteFacilityTag) => {
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: null,
      header: false,
      content: (
        <DeleteWarning
          selectedRowId={facilityTag.id}
          title="Are you sure want to delete this Facility?"
          description="Deleting your Facility is permanent and cannot be undone. All the data, settings, and history will be lost. Are you sure you want to proceed?"
          cancelButtonOnClick={() => setModalBoxStatus('close')}
          submitSvgIcon={<DeleteIcon />}
          submitButtonOnClick={(id) => id && handleDeleteFacilityTag(id)}
        />
      ),
      notificationModal: true,
    });
  };

  /**
   * Charging Station
   * @param facilityTag
   * @returns
   */
  const renderChargingStation = (facilityTag: FacilityTags) => (
    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', cursor: 'pointer' }}>
      {facilityTag?.chargingStations?.map((chargingStation) => (
        <div role='presentation' key={chargingStation._id} onClick={() => navigate(`?stationId=${chargingStation._id}`)}>
          <div className="tag-success-label">
            <span style={{ fontWeight: '700' }}>{chargingStation.name}</span>
          </div>
        </div>
      ))}
    </div>
  );

  /**
   * Handle the Edit Facility Tag function
   */
  const handleFacilityTag = (currentData: SelectedFacilityTag) => {
    setModalBoxDetail({
      title: 'Edit Role',
      content: (
        <EditFacilityTagComponent
          handleCancel={() => setModalBoxStatus('close')}
          selectedFacilityTag={currentData}
        />
      ),
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Renders the custom data for the serial number column
   * @param {FacilityTags} facilityTag - The facilityTag data
   * @param {number} index - The current row index
   * @returns {JSX.Element} The rendered serial number cell
   */
  const renderSerialNumber = (facilityTag: FacilityTags, index: number) => (
    <div className="table-row-each-data">{index + 1}</div>
  );

  /**
   * Handle the openAddFacilityModal function
   */
  const openAddFacilityModal = () => {
    setModalBoxDetail({
      title: 'Add Facility',
      content: <AddFacilityTag handleCancel={() => setModalBoxStatus('close')} />,
      notificationModal: false,
      header: true,
    });
    setModalBoxStatus('open');
  };

  /**
   * Renders the custom data for the actions column
   * @param {EditFacilityTag | DeleteFacilityTag} facilityTag - The facility tag data
   * @returns {JSX.Element} The rendered actions cell
   */
  const renderActions = (facilityTag: SelectedFacilityTag | DeleteFacilityTag) => (
    <div className="action-button-group">
      <div
        className="action-button-item view-color"
        onClick={() => handleFacilityTag(facilityTag as SelectedFacilityTag)}
        role="presentation">
        <ActionEditIcon />
      </div>
      <div
        role="button"
        tabIndex={0}
        aria-label="Delete Facility"
        className="action-button-item"
        onClick={() => handleDeleteFacilityTagWarning(facilityTag as DeleteFacilityTag)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleDeleteFacilityTagWarning(facilityTag as DeleteFacilityTag);
          }
        }}>
        {theme === 'light' ? <ActionDeleteIcon /> : <ActionDeleteIconDark />}
      </div>
    </div>
  );

  const column = [
    {
      label: 'S. No',
      accessorKey: 'sno' as keyof FacilityTags,
      customData: renderSerialNumber,
      columnWidth: 45,
    },
    {
      label: 'Facility Name',
      accessorKey: 'facilityTagName' as keyof FacilityTags,
      sortAllow: true,
    },

    {
      label: 'Location',

      /**
       * Location view in table
       * @param facilityTag
       * @param index
       */
      customData: (facilityTag: FacilityTags) =>
        facilityTag.address ? (
          <div className="location-in-station-table">
            <span>
              <LocationIcon />
            </span>
            <p className="table-row-each-data">{facilityTag.address}</p>
          </div>
        ) : (
          <div />
        ),
      accessorKey: '' as keyof FacilityTags,
      columnWidth: 150,
    },
    {
      label: 'Charging Stations',
      customData: renderChargingStation,
      accessorKey: 'chargingStationsData' as keyof FacilityTags,
      sortAllow: true,
      queryParamsKey: 'stationId',
      queryParamsValue: 'chargingStation',
      sidePanelComponent: <StationSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: 'Actions',
      customData: renderActions,
      accessorKey: '' as keyof FacilityTags,
      columnWidth: 150,
    },
  ];

  useEffect(() => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      limit: DefaultApiLimit.limit,
      page: DefaultApiLimit.page,
    });
  }, [currentFacilityTagsList, filters]);

  return (
    <div className="facility-tag-management-wrap">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={modalBoxDetail.header}
      />
      <div className="facility-tag-management-header">
        <PageTitle
          title="Facility Management"
          buttonLabel="Add Facility"
          buttonSvgIcon={<PlusIcon />}
          buttonType="submit"
          onClickButton={openAddFacilityModal}
        />
      </div>
      <div className="table-container">
        <CustomTable
          paginate
          loading={loading}
          limit={limit}
          header={column}
          data={facilityTags}
          currentPage={page}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{
            sortBy,
            sortField,
          }}
          handleSortChange={handleSortFunction}
          handlePageChange={handlePageChange}
          handleLimitChange={handleLimitChange}
          // footerLeft={
          //   <div className="table-bottom-button-list">
          //     <div className="table-button-item">
          //       <ExportButton
          //         label="Export Data"
          //         buttonList={[
          //           {
          //             key: 'pdf',
          //             allow: true,
          //             /**
          //              * Export pdf function
          //              * @returns
          //              */
          //             onClickFunction: () => exportDataPdfOrCSV('pdf'),
          //           },
          //           {
          //             key: 'csv',
          //             allow: true,
          //             /**
          //              * Export csv function
          //              * @returns
          //              */
          //             onClickFunction: () => exportDataPdfOrCSV('csv'),
          //           },
          //         ]}
          //         disabled={stations.length === 0}
          //         loader={exportLoader}
          //       />
          //     </div>
          //   </div>
          // }
          topHeader={false}
          filters={filters}
          filtersToDispatch={updateFacilityFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListFilters}
          setHeightAutoToTr
        />
      </div>
    </div>
  );
}

export default FacilityTagComponent;
